import { useAppSelector } from "../../app";
import { useMemo } from "react";
import { ICategorization } from "../../configMap/interfaces/category";
import { useTranslation } from "react-i18next";
import selectCategorizations from "../selectors/selectCategorizations";
import { getInLanguageOrDefault } from "../../common/utilities/language";

const useLinkIcon = (categorization: ICategorization) => {
  const {
    t,
    i18n: { language },
  } = useTranslation("annotationSideControls", {
    keyPrefix: "linkedParentCategorization",
  });

  const categorizations = useAppSelector((state) =>
    selectCategorizations(state, { withConfig: false }),
  ) as Array<ICategorization>;

  const parentCategorization = categorization.search?.parentCategorization;

  const linkedChildLabels = useMemo(() => {
    if (!categorizations) {
      return null;
    }

    const linkedCategorizations = categorizations.filter(
      (c) =>
        c.search?.parentCategorization?.id === categorization.id &&
        c.id !== categorization.id, // Exclude self-referencing
    );
    if (!linkedCategorizations?.length) {
      return null;
    }

    return linkedCategorizations.map((lc) =>
      getInLanguageOrDefault(lc.label, language),
    );
  }, [categorizations, categorization, language]);

  const linkedParentLabel = useMemo(() => {
    if (!parentCategorization) {
      return null;
    }

    if (parentCategorization.id === categorization.id) {
      return null;
    }

    const parent = categorizations.find(
      (c) => c.id === parentCategorization.id,
    );
    if (!parent) {
      return null;
    }

    return getInLanguageOrDefault(parent.label, language);
  }, [parentCategorization, categorizations, language, categorization.id]);

  // Mirrors the entity conversion palette: this categorization either drives
  // children (recalculates) or reads from a parent (from).
  const direction = useMemo<"from" | "recalculates" | null>(() => {
    if (linkedChildLabels?.length) {
      return "recalculates";
    }
    if (linkedParentLabel) {
      return "from";
    }
    return null;
  }, [linkedChildLabels, linkedParentLabel]);

  const detail = useMemo(() => {
    if (!direction) {
      return null;
    }
    return direction === "recalculates" ? t("parentDetail") : t("childDetail");
  }, [direction, t]);

  const tagLabel = useMemo(() => {
    if (!direction) {
      return null;
    }
    return direction === "recalculates" ? t("recalculates") : t("from");
  }, [direction, t]);

  const tagValues = useMemo<Array<string>>(() => {
    if (linkedChildLabels?.length) {
      return linkedChildLabels;
    }
    return linkedParentLabel ? [linkedParentLabel] : [];
  }, [linkedChildLabels, linkedParentLabel]);

  return {
    direction,
    header: t("header"),
    detail,
    tagLabel,
    tagValues,
    pillLabel: t("pillLabel"),
  };
};

export default useLinkIcon;
